import { X } from 'lucide-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { components, GetPartyByIdResponse } from 'wg-api-sdk';

import SubmitButton from '@/components/common/SubmitButton';
import { Button } from '@/components/shadcn/button';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import AddressFields from '@/features/party/components/CreatePartyModal/AddressFields';
import LegalPartyFields from '@/features/party/components/CreatePartyModal/LegalPartyFields';
import NaturalPartyFields from '@/features/party/components/CreatePartyModal/NaturalPartyFields';
import { FIELDS_REQUIRED_BY_ANY_CRM } from '@/features/party/components/CreatePartyModal/requiredFieldsByCrm';
import type { CreatePartyValues } from '@/features/party/components/CreatePartyModal/types';
import useBroker from '@/hooks/useBroker';
import hasItems from '@/lib/utils/hasItems';

import type { CRM } from '../../types';

type CountryCode = components['schemas']['COUNTRY_CODES'];

export type ExportPartyTitleKey =
  | 'EXPORT_PARTY_TITLE'
  | 'EXPORT_PARTY_NOT_FOUND_TITLE';

type Props = {
  party: GetPartyByIdResponse;
  crms: Array<{ key: string; name?: string }>;
  titleKey: ExportPartyTitleKey;
  onClose: VoidFunction;
  onSubmit: (
    values: CreatePartyValues,
    changes: { hasPartyChanges: boolean },
  ) => Promise<void>;
};

export default function ExportPartyForm({
  party,
  crms,
  titleKey,
  onClose,
  onSubmit,
}: Props) {
  const { t, i18n } = useTranslation();

  const { broker } = useBroker();

  const isCompany = party.type === 'COMPANY';

  const partyCountryCode = party.address?.country_code as Maybe<CountryCode>;

  const form = useForm<CreatePartyValues>({
    defaultValues: {
      entityType: isCompany ? 'LEGAL' : 'NATURAL',
      email: party.email ?? undefined,
      firstName: party.first_name ?? undefined,
      lastName: party.last_name ?? undefined,
      initials: party.initials ?? undefined,
      birthdate: party.birth?.slice(0, 10),
      companyName: party.name ?? undefined,
      address: {
        street: party.address?.street ?? undefined,
        housenr: party.address?.housenr ?? undefined,
        boxnr: party.address?.boxnr ?? undefined,
        zipcode: party.address?.zipcode ?? undefined,
        city: party.address?.city ?? undefined,
        country_code: partyCountryCode ?? broker?.country_code,
      },
      exportToCrm: Object.fromEntries(
        crms.map((crm) => [crm.key as CRM, true]),
      ),
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, dirtyFields },
  } = form;

  const hasSelectedCrm = Object.values(watch('exportToCrm')).some(Boolean);

  const crmNames = new Intl.ListFormat(i18n.language, {
    type: 'conjunction',
  }).format(crms.map((crm) => crm.name ?? crm.key));

  const submit = (values: CreatePartyValues) => {
    const changedPartyFields = Object.keys(dirtyFields).filter(
      (field) => field !== 'exportToCrm',
    );

    return onSubmit(values, { hasPartyChanges: hasItems(changedPartyFields) });
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(submit)}
        className="relative flex flex-col gap-4 p-4"
      >
        <Button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2"
          variant="ghost"
          size="icon-xs"
        >
          <X />
        </Button>

        <div className="flex flex-col gap-2">
          <p className="pr-6 text-sm font-bold">
            {t(titleKey, { crms: crmNames })}
          </p>

          <Field>
            <FieldLabel htmlFor="email">
              {t('PARTY_CREATE_INLINE_EMAIL_LABEL')}
            </FieldLabel>
            <Input id="email" {...register('email')} />
          </Field>

          {isCompany ? <LegalPartyFields /> : <NaturalPartyFields />}

          <AddressFields />

          <div className="mt-2 flex flex-col">
            {crms.map((crm) => (
              <Controller
                key={`exportToCrm.${crm.key as CRM}`}
                name={`exportToCrm.${crm.key as CRM}`}
                control={form.control}
                rules={{ deps: FIELDS_REQUIRED_BY_ANY_CRM }}
                render={({ field }) => (
                  <Field orientation="horizontal">
                    <Checkbox
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />

                    <FieldLabel htmlFor={field.name} className="cursor-pointer">
                      {t('PARTY_CREATE_INLINE_EXPORT_TO_CRM', {
                        crm: crm.name ?? crm.key,
                      })}
                    </FieldLabel>
                  </Field>
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="grow"
          >
            {t('PARTY_CREATE_INLINE_CANCEL')}
          </Button>

          <SubmitButton
            isSubmitting={isSubmitting}
            disabled={isSubmitting || !hasSelectedCrm}
            className="grow"
          >
            {t('EXPORT_PARTY_SUBMIT')}
          </SubmitButton>
        </div>
      </form>
    </FormProvider>
  );
}
